/**
 * YouTube Video Modal
 * Thumbnail click opens a lazy-loaded YouTube embed in an accessible popup.
 */
(function () {
  'use strict';

  const FOCUSABLE_SELECTOR =
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  class YouTubeVideoModal {
    constructor() {
      this.modal = document.getElementById('ytvm-modal');
      if (!this.modal) return;

      this.overlay = this.modal.querySelector('[data-ytvm-overlay]');
      this.closeButton = this.modal.querySelector('[data-ytvm-close-btn]');
      this.iframeContainer = this.modal.querySelector('[data-ytvm-iframe-container]');
      this.lastFocusedElement = null;
      this.isOpen = false;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      this.overlay?.addEventListener('click', this.onOverlayClick);
      this.closeButton?.addEventListener('click', this.onCloseClick);
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-ytvm-trigger]');
      if (!trigger) return;

      event.preventDefault();
      const videoId = trigger.dataset.ytvmVideoId;
      const title = trigger.dataset.ytvmTitle || 'YouTube video';

      if (!videoId) return;
      this.open(videoId, title, trigger);
    }

    onOverlayClick(event) {
      if (event.target === this.overlay) {
        this.close();
      }
    }

    onCloseClick() {
      this.close();
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    getFocusableElements() {
      return Array.from(this.modal.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute('disabled') && element.offsetParent !== null
      );
    }

    buildEmbedUrl(videoId) {
      const params = new URLSearchParams({
        autoplay: '1',
        rel: '0',
        modestbranding: '1',
        playsinline: '1',
        enablejsapi: '1',
      });

      return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
    }

    open(videoId, title, opener) {
      this.lastFocusedElement = opener;
      this.isOpen = true;

      this.iframeContainer.innerHTML = '';

      const iframe = document.createElement('iframe');
      iframe.className = 'ytvm-modal__iframe';
      iframe.src = this.buildEmbedUrl(videoId);
      iframe.title = title;
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';

      this.iframeContainer.appendChild(iframe);

      this.modal.removeAttribute('hidden');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ytvm-modal-open');

      requestAnimationFrame(() => {
        this.closeButton?.focus();
      });
    }

    close() {
      if (!this.isOpen) return;

      this.isOpen = false;
      this.iframeContainer.innerHTML = '';

      this.modal.setAttribute('hidden', '');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ytvm-modal-open');

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new YouTubeVideoModal());
  } else {
    new YouTubeVideoModal();
  }
})();
